import { type ReactNode, useEffect, useId, useRef, useState } from "react";

/**
 * The shell's popover — **one implementation, two callers** (the account chip and the family
 * switcher), because the #293 wireframes give them the same panel: a bordered card on the
 * surface tone, lifted, opening under a trigger that carries a caret.
 *
 * **A real menu rather than a `<select>`**, and that is a deliberate cost. A native select
 * would be free and accessible, and it cannot render what these menus contain — an avatar and
 * a secondary line per family, a destructive item with its consequence written underneath it.
 * So the keyboard and focus behaviour a select gives away has to be paid for here:
 *
 * - **Escape closes and returns focus to the trigger**, so a keyboard user is never stranded
 *   inside a dismissed popover.
 * - **A click outside closes it**, on `pointerdown` rather than `click` so the dismissal
 *   happens before whatever was clicked reacts.
 * - `aria-expanded` and `aria-haspopup` on the trigger, and a `role="menu"` panel, so the
 *   control announces itself as a menu rather than as a button that mysteriously grows one.
 *
 * **The panel closes on any activation inside it.** Every item here either navigates or ends
 * the session, so a menu that stayed open would be a menu left hanging over the page it just
 * left.
 */
export function Menu({
  label,
  trigger,
  className,
  children,
}: {
  /** What the trigger is, for a screen reader — the visible text is often just an address. */
  label: string;
  trigger: ReactNode;
  /** Applied to the trigger button, so each caller keeps its own shape. */
  className: string;
  /** The panel's contents, rendered only while open. */
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Back to the trigger: closing a menu should not drop focus to the document.
      button.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="menu" ref={root}>
      <button
        ref={button}
        type="button"
        className={className}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((was) => !was)}
      >
        {trigger}
      </button>
      {open ? (
        // `onClick` on the panel rather than on each item: every item in both menus is a
        // navigation or a sign-out, so "any activation closes this" is the rule rather than a
        // property of the item that happened to be clicked. It only ever DISMISSES — every
        // item inside is a link or a button a keyboard reaches on its own, and Escape is
        // handled above.
        // biome-ignore lint/a11y/useKeyWithClickEvents: click here only closes; Escape does too.
        <div id={panelId} className="menu__panel" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** A labelled group inside a menu — "Your families". */
export function MenuLabel({ children }: { children: ReactNode }) {
  return <p className="menu__label">{children}</p>;
}

/** The hairline between a menu's sections. */
export function MenuDivider() {
  return <div className="menu__divider" aria-hidden="true" />;
}
